import { Builder } from '@builder.io/react';
import cn from 'classnames';
import { FC } from 'react';

import { COLOR_NAME_WHITE } from '@/shared/constants/colors';
import { backgroundColorInput, componentLinkIdInput } from '@/shared/inputs';
import { getBuilderSpacingClass } from '@/shared/spacing';
import { isBuilderStringPresent } from '@/shared/text';
import { SharedBuilderComponent } from '@/shared/types';
import { getBuilderIOClassName } from '@/shared/utils/getBuilderIOClassName';

import { Action, ActionVariant, BaseAction } from '../Action';
import sharedStyle from '../shared.module.scss';
import styles from './PageNavigation.module.scss';

export interface PageNavigationProps extends SharedBuilderComponent {
  componentLinkId?: string;
  previousAction?: BaseAction;
  nextAction?: BaseAction;
  locale: string;
}

const hasAction = (action: BaseAction | undefined, locale: string) =>
  Boolean(
    action &&
    isBuilderStringPresent(action.label, locale) &&
    isBuilderStringPresent(action.url, locale)
  );

export const PageNavigation: FC<PageNavigationProps> = ({
  componentLinkId,
  previousAction,
  nextAction,
  locale,
  attributes,
  backgroundColorClassName,
  builderBlock,
  builderState,
}) => {
  const hasPreviousAction = hasAction(previousAction, locale);
  const hasNextAction = hasAction(nextAction, locale);

  if (!hasPreviousAction && !hasNextAction) return null;

  return (
    <nav
      {...attributes}
      id={componentLinkId}
      aria-label="Page navigation"
      data-testid="pageNavigation"
      className={cn(
        sharedStyle.section,
        getBuilderIOClassName(attributes),
        getBuilderSpacingClass(builderBlock, builderState),
        backgroundColorClassName
      )}
    >
      <div className="u-wrapper">
        <div className={styles.actions}>
          {hasPreviousAction && (
            <div className={styles.previousAction}>
              <Action
                {...previousAction}
                locale={locale}
                variant={ActionVariant.SECONDARY}
              />
            </div>
          )}
          {hasNextAction && (
            <div className={styles.nextAction}>
              <Action
                {...nextAction}
                locale={locale}
                variant={ActionVariant.PRIMARY}
              />
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export const NAME = 'PageNavigation';

const navigationActionInput = (
  name: 'previousAction' | 'nextAction',
  friendlyName: string,
  label: string,
  url: string
) => ({
  name,
  friendlyName,
  type: 'object',
  defaultValue: { label, url },
  subFields: [
    {
      name: 'label',
      friendlyName: 'Label',
      type: 'CatawikiPlainEditor',
      defaultValue: label,
      localized: true,
      required: true,
    },
    {
      name: 'url',
      friendlyName: 'URL',
      type: 'CatawikiPlainEditor',
      defaultValue: url,
      localized: true,
      required: true,
    },
  ],
});

export function componentRegister() {
  Builder.registerComponent(PageNavigation, {
    name: NAME,
    inputs: [
      componentLinkIdInput(),
      navigationActionInput(
        'previousAction',
        'Previous page',
        'Previous chapter',
        '/previous'
      ),
      navigationActionInput('nextAction', 'Next page', 'Next chapter', '/next'),
      backgroundColorInput(COLOR_NAME_WHITE),
    ],
    noWrap: true,
  });
  return NAME;
}

export function dataFetcher(locale: string) {
  return async () => ({ locale });
}

export const componentDataFetchers = (locale: string) => ({
  [NAME]: dataFetcher(locale),
});
